import get from "es-toolkit/compat/get";
import {
  isRecord,
  toNumber,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type { VizDatasetRow } from "@/lib/ai/visualization/data/schema";

export type BindingPath = string | readonly (string | number)[];

export type RawCategoryField = {
  key: string;
  read: (item: Record<string, unknown>) => string | null;
};

/**
 * raw adapter が扱う文字列値を抽出する。
 *
 * @remarks
 * dataset row の許容型（string | number | null）へ素直に写すため、
 * ここでは表示目的のフォーマットは行わない。
 */
export function toRawText(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

/**
 * path 候補の先頭一致から文字列を読む。
 */
export function readFirstRawText(
  source: Record<string, unknown>,
  paths: readonly BindingPath[],
): string | null {
  for (const path of paths) {
    const value = toRawText(get(source, path));
    if (value !== null) return value;
  }
  return null;
}

/**
 * path 候補の先頭一致から boolean を "true" | "false" へ写す。
 */
export function readFirstRawBooleanText(
  source: Record<string, unknown>,
  paths: readonly BindingPath[],
): string | null {
  for (const path of paths) {
    const value = get(source, path);
    if (value === true) return "true";
    if (value === false) return "false";
  }
  return null;
}

/**
 * path 候補の先頭一致から数値を読む。
 */
export function readFirstRawNumber(
  source: Record<string, unknown>,
  paths: readonly BindingPath[],
): number | null {
  for (const path of paths) {
    const value = toNumber(get(source, path));
    if (value !== null) return value;
  }
  return null;
}

/**
 * 入力 items から最初に値が見つかる category key を返す。
 */
export function resolveRawCategoryKey(args: {
  items: unknown[];
  categories: readonly RawCategoryField[];
  fallbackKey?: string;
}): string {
  const fallbackKey = args.fallbackKey ?? "dimension";
  for (const category of args.categories) {
    const found = args.items.some(
      (item) => isRecord(item) && category.read(item) !== null,
    );
    if (found) return category.key;
  }
  return fallbackKey;
}

/**
 * 選択 category を優先し、欠損時は category 一覧を順にフォールバックする。
 */
export function resolveRawCategoryValue(args: {
  item: Record<string, unknown>;
  selectedCategoryKey: string;
  categories: readonly RawCategoryField[];
}): string | null {
  const selected = args.categories.find(
    (category) => category.key === args.selectedCategoryKey,
  );
  if (selected) {
    const value = selected.read(args.item);
    if (value !== null) return value;
  }

  for (const category of args.categories) {
    const value = category.read(args.item);
    if (value !== null) return value;
  }
  return null;
}

/**
 * orderBy 指定に従って rows を安定ソートする。
 *
 * @remarks
 * metric が存在しない場合は元順を維持する。比較不能な行は末尾へ送る。
 */
export function sortRowsByPreferredMetric(args: {
  rows: VizDatasetRow[];
  categoryKey: string;
  preferredMetricKey: string | null;
  orderByDirection: string | null;
}): VizDatasetRow[] {
  const { preferredMetricKey } = args;
  if (!preferredMetricKey) return args.rows;

  const hasMetric = args.rows.some(
    (row) => typeof row[preferredMetricKey] === "number",
  );
  if (!hasMetric) return args.rows;

  const direction = args.orderByDirection === "ASC" ? 1 : -1;
  return [...args.rows].sort((left, right) => {
    const leftValue = left[preferredMetricKey];
    const rightValue = right[preferredMetricKey];
    const leftMetric = typeof leftValue === "number" ? leftValue : null;
    const rightMetric = typeof rightValue === "number" ? rightValue : null;

    if (leftMetric === null && rightMetric === null) {
      return String(left[args.categoryKey] ?? "").localeCompare(
        String(right[args.categoryKey] ?? ""),
      );
    }
    if (leftMetric === null) return 1;
    if (rightMetric === null) return -1;

    const diff = direction * (leftMetric - rightMetric);
    if (diff !== 0) return diff;
    return String(left[args.categoryKey] ?? "").localeCompare(
      String(right[args.categoryKey] ?? ""),
    );
  });
}
